import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Search, FileCheck, CheckCircle, ArrowRight } from 'lucide-react';
import { testimonials, processSteps, images, countryCodes } from '../mock';
import PricingSection from '../components/PricingSection';
import { Button } from '../components/ui/button';
import AnimatedBackground from '../components/AnimatedBackground';
import { fetchGeolocation, submitLead, getDialCodeFromCountry } from '../services/apiService';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const LandingPage = ({ onOpenPopup }) => {
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentProcess, setCurrentProcess] = useState(0);
  const [currentFeature, setCurrentFeature] = useState(0);
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const features = [
    { icon: Shield, title: "Expert Legal Protection", desc: "Our trademark attorneys have 10+ years of experience ensuring your brand gets the strongest legal protection available." },
    { icon: Search, title: "Comprehensive Search", desc: "Thorough trademark searches across federal and state databases to identify potential conflicts before filing." },
    { icon: ArrowRight, title: "Fast Processing", desc: "Your application filed within 24-48 hours. We prioritize speed without compromising on accuracy or quality." },
    { icon: CheckCircle, title: "Monitoring & Support", desc: "Ongoing trademark monitoring and dedicated support throughout the entire registration process and beyond." },
    { icon: FileCheck, title: "Transparent Pricing", desc: "No hidden fees or surprises. Clear, upfront pricing so you know exactly what you're paying for." },
    { icon: Shield, title: "Office Action Response", desc: "Expert handling of USPTO office actions to maximize your chances of approval and overcome any objections." }
  ];

  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    // Mobile sliders
    const isMobile = window.innerWidth < 768;
    let processInterval, featureInterval;
    
    if (isMobile) {
      processInterval = setInterval(() => {
        setCurrentProcess((prev) => (prev + 1) % 3); // Only 3 steps
      }, 4000);
      
      featureInterval = setInterval(() => {
        setCurrentFeature((prev) => (prev + 1) % features.length);
      }, 4000);
    }

    // Fetch geolocation on mount
    const loadGeolocation = async () => {
      const result = await fetchGeolocation();
      if (result.success) {
        setGeoData(result.data);
      }
    };
    loadGeolocation();

    return () => {
      clearInterval(testimonialInterval);
      if (processInterval) clearInterval(processInterval);
      if (featureInterval) clearInterval(featureInterval);
    };
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target);
    const name = formData.get('name');
    const email = formData.get('email');
    const countryCode = formData.get('countryCode');
    const phoneNumber = formData.get('phoneNumber');
    const message = formData.get('message');

    // Find country from countryCodes
    const selectedCountry = countryCodes.find(c => c.code === countryCode);
    const dialCode = selectedCountry ? parseInt(selectedCountry.code.replace('+', '')) : 1;
    const country = selectedCountry ? selectedCountry.country.split(' ')[0].substring(0, 2).toUpperCase() : 'US';

    const leadData = {
      name,
      email,
      countryDialCode: dialCode,
      country,
      phoneNumber,
      ipAddress: geoData?.ipAddress || '',
      description: message || '',
      city: geoData?.cityName || '',
      state: geoData?.regionName || '',
      geoLocation: geoData ? {
        isSuccess: true,
        data: geoData,
        errorMessage: '',
        statusCode: 200
      } : null
    };

    const result = await submitLead(leadData);
    setIsSubmitting(false);

    if (result.success) {
      toast.success(result.message || 'Thank you! We will contact you shortly.');
      e.target.reset();
      // Redirect to application page
      // navigate(`/application/main?customerid=${result.customerId}`);
      window.location = `/application/main?customerid=${result.customerId}`
    } else {
      toast.error(result.error || 'Failed to submit. Please try again.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Your Brand Deserves Legal Protection | Trusted Trademark Filer</title>
        <meta name="description" content="Register your trademark with confidence. Expert attorneys, transparent pricing, 95% success rate. Fast processing in 24-48 hours. Protect your brand today." />
      </Helmet>
      <div className="min-h-screen">
      {/* Hero Section - Responsive */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        {/* Background Pattern - Slightly Darker */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#B8E6F5]/95 via-[#E8F4F8]/95 to-[#FFD4C8]/95"></div>
        
        {/* Animated Geometric Shapes - Hidden on mobile */}
        <div className="hidden md:block absolute inset-0 opacity-20">
          {/* Circle 1 */}
          <div className="absolute top-20 left-10 w-64 h-64 border-4 border-[#FF4A2A] rounded-full animate-pulse" style={{animationDuration: '3s'}}></div>
          
          {/* Square 1 */}
          <div className="absolute top-40 right-20 w-48 h-48 border-4 border-blue-500 rotate-45 animate-spin" style={{animationDuration: '20s'}}></div>
          
          {/* Triangle (using border trick) */}
          <div className="absolute bottom-32 left-1/4 w-0 h-0 border-l-[60px] border-l-transparent border-r-[60px] border-r-transparent border-b-[100px] border-b-orange-400 animate-bounce" style={{animationDuration: '4s'}}></div>
          
          {/* Circle 2 */}
          <div className="absolute bottom-20 right-1/4 w-40 h-40 bg-gradient-to-br from-purple-300 to-pink-300 rounded-full animate-pulse" style={{animationDuration: '2.5s', animationDelay: '0.5s'}}></div>
          
          {/* Rectangle */}
          <div className="absolute top-1/3 left-1/3 w-56 h-32 border-3 border-teal-400 rounded-lg animate-pulse" style={{animationDuration: '3.5s', animationDelay: '1s'}}></div>
          
          {/* Small Circle 3 */}
          <div className="absolute top-1/2 right-1/3 w-32 h-32 border-4 border-yellow-400 rounded-full animate-bounce" style={{animationDuration: '3s', animationDelay: '1.5s'}}></div>
          
          {/* Hexagon-like shape */}
          <div className="absolute bottom-1/4 left-1/2 w-44 h-44 border-4 border-green-400 rotate-12 animate-spin" style={{animationDuration: '25s'}}></div>
        </div>
        
        {/* Animated Abstract Icons Background - Hidden on mobile */}
        <div className="hidden md:block relative z-0">
          <AnimatedBackground />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start lg:items-center">
            {/* Left Side - Content */}
            <div className="space-y-4 md:space-y-6">
              <div className="inline-block">
                <span className="bg-gradient-to-r from-[#FF4A2A] to-[#FF6B4A] text-white px-4 py-2 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-semibold shadow-lg">
                  ⚡ Fast • Reliable • Expert Service
                </span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Your Brand Deserves<br />
                <span className="text-[#FF4A2A]">Legal Protection</span>
              </h1>
              
              <p className="text-base md:text-xl text-gray-700 leading-relaxed">
                Register your trademark with confidence. Expert attorneys, transparent pricing, and a proven 95% success rate.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-2 md:pt-4">
                <Button
                  onClick={onOpenPopup}
                  className="bg-gradient-to-r from-[#FF4A2A] to-[#FF6B4A] hover:from-[#E63E1F] hover:to-[#FF4A2A] text-white px-6 py-4 md:px-10 md:py-6 text-base md:text-lg font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  Start Protection Now
                  <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
                </Button>
                <Button
                  onClick={() => window.openChatWidget && window.openChatWidget()}
                  className="border-3 border-[#FF4A2A] text-[#FF4A2A] hover:bg-[#FF4A2A] hover:text-white px-6 py-4 md:px-10 md:py-6 text-base md:text-lg font-bold rounded-xl transition-all duration-300"
                >
                  Chat Now
                </Button>
              </div>
              
              <div className="grid grid-cols-3 gap-4 md:gap-6 pt-4 md:pt-6">
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#FF4A2A] mb-1">95%</div>
                  <div className="text-xs md:text-sm text-gray-600">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#FF4A2A] mb-1">24-48hr</div>
                  <div className="text-xs md:text-sm text-gray-600">Fast Filing</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#FF4A2A] mb-1">500+</div>
                  <div className="text-xs md:text-sm text-gray-600">Brands Secured</div>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Start Your Trademark Registration
              </h2>
              {geoData && (
                <div className="mb-4 p-3 bg-[#E2F8FE] rounded-lg border border-[#FF4A2A]/20">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="font-semibold">📍 Detected Location:</span>
                    <span>{geoData.countryName}</span>
                  </div>
                </div>
              )}
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label htmlFor="lp-hero-name" className="block text-base font-medium text-gray-700 mb-2">
                    Enter Your Name *
                  </label>
                  <input
                    id="lp-hero-name"
                    name="name"
                    type="text"
                    required
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF4A2A] focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="lp-hero-email" className="block text-base font-medium text-gray-700 mb-2">
                    Enter Your Email *
                  </label>
                  <input
                    id="lp-hero-email"
                    name="email"
                    type="email"
                    required
                    placeholder="Enter your email address"
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF4A2A] focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="lp-hero-phone" className="block text-base font-medium text-gray-700 mb-2">
                    Number *
                  </label>
                  <div className="flex gap-2">
                    <select name="countryCode" className="px-3 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF4A2A] w-40">
                      {countryCodes.map((country, index) => (
                        <option key={index} value={country.code}>
                          {country.code} ({country.country})
                        </option>
                      ))}
                    </select>
                    <input
                      id="lp-hero-phone"
                      name="phoneNumber"
                      type="text"
                      required
                      placeholder="Enter Your Number"
                      maxLength="10"
                      pattern="\d{10}"
                      onInput={(e) => e.target.value = e.target.value.replace(/\D/g, '')}
                      className="flex-1 px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF4A2A] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="lp-hero-message" className="block text-base font-medium text-gray-700 mb-2">
                    What are you looking for?
                  </label>
                  <textarea
                    id="lp-hero-message"
                    name="message"
                    rows="3"
                    placeholder="e.g., name, logo, slogan"
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF4A2A] focus:border-transparent"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FF4A2A] hover:bg-[#E63E1F] disabled:bg-gray-400 text-white px-8 py-4 text-base font-semibold rounded-lg transition-colors duration-300 shadow-lg"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Trademark Protection - With Image */}
      <section className="py-24 bg-gradient-to-b from-white to-[#E2F8FE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Image */}
            <div className="relative">
              <div className="absolute -top-8 -left-8 w-64 h-64 bg-[#FF4A2A] rounded-full opacity-10 blur-3xl"></div>
              <div className="relative z-10">
                <img
                  src={images.about}
                  alt="Trademark Protection"
                  className="rounded-3xl shadow-2xl w-full h-[450px] object-cover border-4 border-white"
                />
              </div>
            </div>

            {/* Right Side - Content */}
            <div className="space-y-6">
              <div className="inline-block bg-[#E2F8FE] rounded-full px-6 py-2">
                <span className="text-[#FF4A2A] font-semibold text-sm">TRADEMARK PROTECTION</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Understanding Trademark Protection
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                A trademark is your business's unique identifier that sets you apart from competitors. Learn why protecting it is crucial for your brand's success and long-term growth.
              </p>

              <div className="space-y-4 pt-4">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-[#FF4A2A] to-[#FF6B4A] p-3 rounded-xl flex-shrink-0">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 text-lg">Exclusive Nationwide Rights</h3>
                    <p className="text-base text-gray-600 leading-relaxed">Federal registration grants exclusive rights to use your mark across all 50 states, protecting your brand from coast to coast.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-[#FF4A2A] to-[#FF6B4A] p-3 rounded-xl flex-shrink-0">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 text-lg">Legal Protection & Enforcement</h3>
                    <p className="text-base text-gray-600 leading-relaxed">Gain legal presumption of ownership and the right to sue infringers in federal court with statutory damages.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-[#FF4A2A] to-[#FF6B4A] p-3 rounded-xl flex-shrink-0">
                    <ArrowRight className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 text-lg">Valuable Business Asset</h3>
                    <p className="text-base text-gray-600 leading-relaxed">Trademarks can be licensed, sold, or used as collateral to increase your company's value and open new revenue streams.</p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Button
                  onClick={onOpenPopup}
                  className="bg-[#FF4A2A] hover:bg-[#E63E1F] text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-lg"
                >
                  Learn More About Protection
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Enhanced */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#FF4A2A] rounded-full px-6 py-2 mb-4">
              <span className="text-white font-semibold text-sm">WHY CHOOSE US</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Complete Trademark Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From initial search to final registration, we provide comprehensive trademark services tailored to protect your brand
            </p>
          </div>

          {/* Desktop - Show all 6 features */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-orange-100 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF4A2A] opacity-5 rounded-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="bg-gradient-to-br from-[#FF4A2A] to-[#FF6B4A] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Expert Legal Protection
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  Our trademark attorneys have 10+ years of experience ensuring your brand gets the strongest legal protection available.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-orange-100 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF4A2A] opacity-5 rounded-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="bg-gradient-to-br from-[#FF4A2A] to-[#FF6B4A] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Search className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Comprehensive Search
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  Thorough trademark searches across federal and state databases to identify potential conflicts before filing.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-orange-100 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF4A2A] opacity-5 rounded-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="bg-gradient-to-br from-[#FF4A2A] to-[#FF6B4A] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <ArrowRight className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Fast Processing
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  Your application filed within 24-48 hours. We prioritize speed without compromising on accuracy or quality.
                </p>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-orange-100 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF4A2A] opacity-5 rounded-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="bg-gradient-to-br from-[#FF4A2A] to-[#FF6B4A] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <CheckCircle className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Monitoring & Support
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  Ongoing trademark monitoring and dedicated support throughout the entire registration process and beyond.
                </p>
              </div>
            </div>

            {/* Feature 5 */}
            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-orange-100 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF4A2A] opacity-5 rounded-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="bg-gradient-to-br from-[#FF4A2A] to-[#FF6B4A] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <FileCheck className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Transparent Pricing
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  No hidden fees or surprises. Clear, upfront pricing so you know exactly what you're paying for.
                </p>
              </div>
            </div>

            {/* Feature 6 */}
            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-orange-100 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF4A2A] opacity-5 rounded-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="bg-gradient-to-br from-[#FF4A2A] to-[#FF6B4A] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Office Action Response
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  Expert handling of USPTO office actions to maximize your chances of approval and overcome any objections.
                </p>
              </div>
            </div>
          </div>

          {/* Mobile - Slider */}
          <div className="md:hidden">
            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg border-2 border-orange-100 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF4A2A] opacity-5 rounded-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="bg-gradient-to-br from-[#FF4A2A] to-[#FF6B4A] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  {React.createElement(features[currentFeature].icon, { className: "h-8 w-8 text-white" })}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {features[currentFeature].title}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  {features[currentFeature].desc}
                </p>
              </div>
              
              {/* Dots */}
              <div className="flex justify-center gap-2 mt-8">
                {features.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentFeature(index)}
                    className={`h-3 rounded-full transition-all duration-300 ${
                      index === currentFeature ? 'w-12 bg-[#FF4A2A]' : 'w-3 bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mid-Page CTA Banner */}
      <section className="py-20 bg-gradient-to-r from-[#FF4A2A] via-[#FF6B4A] to-[#FF4A2A] relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern opacity-10"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-3xl p-12 border border-white border-opacity-20 shadow-2xl">
            <h2 className="text-4xl md:text-4xl font-bold text-white mb-6">
              Don't Wait - Protect Your Brand Now
            </h2>
            <p className="text-xl text-white opacity-90 mb-8 max-w-2xl mx-auto">
              Every day without trademark protection puts your brand at risk. Secure your intellectual property today and gain peace of mind for tomorrow.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={onOpenPopup}
                className="bg-white text-[#FF4A2A] hover:bg-gray-100 px-10 py-7 text-lg font-semibold rounded-xl shadow-xl hover:scale-105 transition-all duration-300"
              >
                Register My Trademark
              </Button>
              <Button
                onClick={() => window.openChatWidget && window.openChatWidget()}
                className="border-2 border-white text-white hover:bg-white hover:text-[#FF4A2A] px-10 py-7 text-lg font-semibold rounded-xl transition-all duration-300 hover:scale-105"
              >
                Chat Now
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div className="text-white">
                <div className="text-xl font-bold mb-2">95%</div>
                <div className="text-sm opacity-90">Approval Rate</div>
              </div>
              <div className="text-white">
                <div className="text-xl font-bold mb-2">24-48hr</div>
                <div className="text-sm opacity-90">Filing Time</div>
              </div>
              <div className="text-white">
                <div className="text-xl font-bold mb-2">$45+</div>
                <div className="text-sm opacity-90">Starting Price</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps - 3 Steps, Desktop No Slide, Mobile Slider */}
      <section className="py-24 bg-gradient-to-b from-white to-[#E2F8FE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#E2F8FE] rounded-full px-6 py-2 mb-4">
              <span className="text-[#FF4A2A] font-semibold text-base">HOW IT WORKS</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Simple 3-Step Process
            </h2>
            <p className="text-xl text-gray-600">
              We make trademark registration straightforward and hassle-free
            </p>
          </div>

          {/* Desktop - Show all 3 steps side by side */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            {processSteps.slice(0, 3).map((step, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 hover:shadow-2xl transition-all duration-300">
                <div className="flex items-center justify-center mb-6">
                  <div className="bg-gradient-to-br from-[#FF4A2A] to-[#E63E1F] text-white text-2xl font-bold w-16 h-16 rounded-full flex items-center justify-center shadow-lg">
                    {index + 1}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                  {step.title}
                </h3>
                <p className="text-base text-gray-600 leading-relaxed text-center">
                  {step.description}
                </p>
              </div>
            ))}
          </div>

          {/* Mobile - Slider */}
          <div className="md:hidden">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
              <div className="flex items-center justify-center mb-6">
                <div className="bg-gradient-to-br from-[#FF4A2A] to-[#E63E1F] text-white text-2xl font-bold w-16 h-16 rounded-full flex items-center justify-center shadow-lg">
                  {currentProcess + 1}
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                {processSteps[currentProcess].title}
              </h3>
              <p className="text-base text-gray-600 leading-relaxed text-center">
                {processSteps[currentProcess].description}
              </p>
              
              {/* Dots for mobile slider */}
              <div className="flex justify-center gap-2 mt-8">
                {processSteps.slice(0, 3).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentProcess(index)}
                    className={`h-3 rounded-full transition-all duration-300 ${
                      index === currentProcess ? 'w-12 bg-[#FF4A2A]' : 'w-3 bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection onOpenPopup={onOpenPopup} />

      {/* Testimonials - 3 at a time with auto-slide */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#E2F8FE] rounded-full px-6 py-2 mb-4">
              <span className="text-[#FF4A2A] font-semibold text-sm">TESTIMONIALS</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Business Owners
            </h2>
            <p className="text-xl text-gray-600">
              See what our clients say about their experience
            </p>
          </div>

          {/* Desktop - Show 3 testimonials at a time with sliding */}
          <div className="hidden md:block relative">
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.slice(currentTestimonial, currentTestimonial + 3).concat(
                currentTestimonial + 3 > testimonials.length 
                  ? testimonials.slice(0, (currentTestimonial + 3) - testimonials.length)
                  : []
              ).slice(0, 3).map((testimonial, index) => (
                <div key={index} className="bg-gradient-to-br from-[#E2F8FE] to-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-all duration-500">
                  <div className="flex justify-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-[#FF4A2A] text-lg">★</span>
                    ))}
                  </div>
                  <p className="text-base text-gray-700 italic leading-relaxed mb-6 text-center">
                    "{testimonial.text}"
                  </p>
                  <div className="pt-4 border-t border-gray-200 text-center">
                    <p className="font-bold text-gray-900">
                      {testimonial.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {testimonial.company}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Dots indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial ? 'w-12 bg-[#FF4A2A]' : 'w-3 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Mobile - Single testimonial slider */}
          <div className="md:hidden">
            <div className="bg-gradient-to-br from-[#E2F8FE] to-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="flex justify-center mb-4">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <span key={i} className="text-[#FF4A2A] text-xl">★</span>
                ))}
              </div>
              <p className="text-lg text-gray-700 italic leading-relaxed mb-6 text-center">
                "{testimonials[currentTestimonial].text}"
              </p>
              <div className="pt-6 border-t border-gray-200 text-center">
                <p className="font-bold text-gray-900 text-lg">
                  {testimonials[currentTestimonial].name}
                </p>
                <p className="text-gray-600">
                  {testimonials[currentTestimonial].company}
                </p>
              </div>
              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`h-3 rounded-full transition-all duration-300 ${
                      index === currentTestimonial ? 'w-12 bg-[#FF4A2A]' : 'w-3 bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - New Style */}
      <section className="py-24 bg-gradient-to-br from-white via-[#E2F8FE] to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#FF4A2A] rounded-full px-6 py-2 mb-4">
              <span className="text-white font-semibold text-sm">FAQ</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about trademark registration
            </p>
          </div>

          <div className="space-y-4">
            {/* FAQ 1 */}
            <div className="bg-white rounded-2xl shadow-lg border-l-4 border-[#FF4A2A] hover:shadow-xl transition-all duration-300">
              <div className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-[#FF4A2A] text-white rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 font-bold text-lg">
                    Q1
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      How long does trademark registration take?
                    </h3>
                    <p className="text-gray-600 text-base leading-relaxed">
                      The entire process typically takes 8-12 months, though it can vary based on USPTO workload and any office actions. We file your application within 24-48 hours and keep you updated throughout the entire process.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ 2 */}
            <div className="bg-white rounded-2xl shadow-lg border-l-4 border-[#FF4A2A] hover:shadow-xl transition-all duration-300">
              <div className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-[#FF4A2A] text-white rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 font-bold text-lg">
                    Q2
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      What is included in the government filing fee?
                    </h3>
                    <p className="text-gray-600 text-base leading-relaxed">
                      The USPTO charges a filing fee per class of goods/services, which is separate from our service fees. We'll guide you on the exact costs and help you choose the right filing option for your needs.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ 3 */}
            <div className="bg-white rounded-2xl shadow-lg border-l-4 border-[#FF4A2A] hover:shadow-xl transition-all duration-300">
              <div className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-[#FF4A2A] text-white rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 font-bold text-lg">
                    Q3
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Can I trademark a logo and business name together?
                    </h3>
                    <p className="text-gray-600 text-base leading-relaxed">
                      Yes, you can file for both separately or as a combined mark. We'll advise you on the best strategy for your specific situation to ensure maximum protection for your brand.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ 4 */}
            <div className="bg-white rounded-2xl shadow-lg border-l-4 border-[#FF4A2A] hover:shadow-xl transition-all duration-300">
              <div className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-[#FF4A2A] text-white rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 font-bold text-lg">
                    Q4
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      What if my trademark application is rejected?
                    </h3>
                    <p className="text-gray-600 text-base leading-relaxed">
                      We handle office actions and work with you to address any issues. Our high approval rate comes from thorough pre-filing searches and expert application preparation to minimize rejection risks.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ 5 */}
            <div className="bg-white rounded-2xl shadow-lg border-l-4 border-[#FF4A2A] hover:shadow-xl transition-all duration-300">
              <div className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-[#FF4A2A] text-white rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 font-bold text-lg">
                    Q5
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Do I need a lawyer to register a trademark?
                    </h3>
                    <p className="text-gray-600 text-base leading-relaxed">
                      While not required, working with trademark professionals significantly increases your success rate and ensures proper protection of your brand. Our experts handle all complexities for you.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Banner - Full Width, Reduced Height */}
      <section className="relative bg-gradient-to-r from-[#FF4A2A] to-[#FF6B4A] py-12 overflow-hidden">
        {/* Animated Gradient Orbs */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-orange-200 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 text-white">
            {/* Left Side - Content */}
            <div className="flex-1 space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold leading-tight">
                Ready to Protect Your Brand?
              </h2>
              <p className="text-lg md:text-xl opacity-95 leading-relaxed max-w-2xl">
                Join 500+ successful businesses. Start your trademark registration in just 2 minutes.
              </p>
              <div className="flex flex-wrap items-center gap-6 pt-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-sm font-medium">95% Success Rate</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-sm font-medium">24-48hr Filing</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-sm font-medium">Expert Support</span>
                </div>
              </div>
            </div>
            
            {/* Right Side - CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 lg:flex-shrink-0">
              <Button
                onClick={onOpenPopup}
                className="bg-white text-[#FF4A2A] hover:bg-gray-100 px-8 py-4 text-lg font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                Start Registration Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={() => window.openChatWidget && window.openChatWidget()}
                className="border-2 border-white text-white hover:bg-white hover:text-[#FF4A2A] px-8 py-4 text-lg font-bold rounded-xl transition-all duration-300"
              >
                Talk to Expert
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Footer for Landing Page */}
      <footer className="bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Left - Logo and Copyright */}
            <div className="flex flex-col items-center md:items-start gap-2">
              <img src="/assets/img/brand/logo-light.png" alt="Trusted Trademark Filer" className="h-10" />
              <div className="text-gray-400 text-sm">
                © 2026 Trusted Trademark Filer. All Rights Reserved
              </div>
            </div>
            
            {/* Right - Links */}
            <div className="flex gap-6">
              <a href="/privacy-policy" className="text-gray-400 hover:text-white text-sm transition-colors duration-300">
                Privacy Policy
              </a>
              <span className="text-gray-600">|</span>
              <a href="/terms-of-service" className="text-gray-400 hover:text-white text-sm transition-colors duration-300">
                Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;